import React, {useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DayPickerSingleDateController } from "react-dates";
import OutsideClickHandler from "react-outside-click-handler";
import { Alert } from "../../../components/Alert";
import { Button, NakedButton } from "../../../components/Button";
import { CheckboxInput } from "../../../components/CheckboxInput";
import {
  Container,
  ColContainer,
  TPL_THIRDS,
} from "../../../components/Container";
import { LoadingError } from "../../../components/LoadingFork";
import { HR, H3 } from "../../../components/Typography";
import { Input } from "../../../components/Input";
import { Label } from "../../../components/Label";
import { SelectInput } from "../../../components/SelectInput";
import { useInstallation } from "../../../components/InstallationContext";
import { isConciergePageEnabled } from "../../../lib/conciergeFeature";
import { bookingContactsQuery } from "../../Concierge/vip-routing/queries";
import { Table, THead, TBody } from "../../../components/Table";
import { css } from "glamor";
import { useFormState } from "react-use-form-state";
import { useMutation, useQuery } from "@apollo/client";
import { withAppConfig } from "../../../components/AppConfigContext";
import ReactToPrint, { PrintContextConsumer } from "react-to-print";
import QRCode from "react-qr-code";

import * as enums from "../../../lib/enums";
import {
  determineOptionals,
  formatCurrency,
  formatHours,
} from "../../../lib/rateCardUtils";
import { allRateCards, createRateCardMutation,
  bookingUiV2EnabledQuery, extraImageUploadUrlMutation } from "./queries";
import * as theme from "../../../theme";
import { flavours } from "../../../theme";
import { RateCardExtraContainer } from "../../../components/RateCardExtras";
import { DateInput } from "../../../components/DateInput";
import { allLots } from "../ParkingLots/queries";
import authManager, {PERMISSIONS} from "../../../lib/authManager";
import ButtonInput from "../../../components/ButtonInput";
import {PermissionOnly} from "../../../components/AdminOnly";

const CF_CSS = css({
  display: "flex",
  alignItems: "center",
  borderWidth: 2,
  borderStyle: "solid",
  borderColor: "#FAFAFA",
  borderRadius: 6,
  fontSize: 22,
  fontWeight: "bold",
  height: 60,
  paddingLeft: 16,
  paddingRight: 16,
  marginBottom: 16,
});

function CurrencyField({ label, value, currency }) {
  value = Number.isFinite(value) ? formatCurrency(value, currency) : "0.00";
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className={CF_CSS}>{value}</div>
    </div>
  );
}

export const jsonValueSafe = (value, defaultValue) => {
  if (!value) return defaultValue;

  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      return parsedValue ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  return value;
};

export function validateAUD(value) {
  return value && isFinite(value) ? undefined : "Please enter a number";
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

let _cdrRowIdCounter = 0;
const nextCdrRowId = () => `cdr-${++_cdrRowIdCounter}-${Date.now()}`;

// Group flat [{date, rate}] entries by rate so loaded data shows as multi-date rows.
export function groupCustomDayRatesByRate(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return [];
  const byRate = new Map();
  for (const entry of flat) {
    if (!entry || typeof entry.date !== "string") continue;
    const rate = entry.rate == null ? "0" : String(entry.rate);
    if (!byRate.has(rate)) byRate.set(rate, []);
    byRate.get(rate).push(entry.date);
  }
  return Array.from(byRate.entries()).map(([rate, dates]) => ({
    id: nextCdrRowId(),
    dates: dates.slice().sort(),
    rate,
  }));
}

export function flattenCustomDayRatesRows(rows) {
  const flat = [];
  for (const row of rows) {
    if (!row || !Array.isArray(row.dates)) continue;
    for (const date of row.dates) {
      flat.push({ date, rate: String(row.rate ?? "0") });
    }
  }
  return flat;
}

export function findDuplicateCustomDayRateDate(rows) {
  const seen = new Set();
  for (const row of rows) {
    for (const d of row?.dates || []) {
      if (seen.has(d)) return d;
      seen.add(d);
    }
  }
  return null;
}

/*
 * Translate raw server / GraphQL error messages from the rate-card create
 * mutation into plain-English text the operator can act on. Falls back to
 * the cleaned-up original message when no pattern matches.
 */
export function toFriendlyRateCardError(error) {
  if (!error) return null;
  if (error.networkError) {
    return "There was a problem contacting the server. Please try again.";
  }
  const raw =
    (error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
    error.message ||
    String(error);
  const cleaned = raw
    .replace(/^GraphQL error:\s*/i, "")
    .replace(/^Query has invalid fields\s*—?\s*/i, "")
    .replace(/^INVALID_PARAMS\s*—?\s*/i, "")
    .replace(/^INVALID PARAMS\s*—?\s*/i, "")
    .trim();

  const patterns = [
    [/fixedSessionFullDayFeeAud cannot be less than fixedSessionPriceAud/i,
      "The Full-day fee must be greater than or equal to the Charge to Guest amount."],
    [/fixedSessionFullDayFeeAud must be a non-negative number/i,
      "The Full-day fee must be zero or greater."],
    [/fixedSessionPriceAud must be a non-negative number/i,
      "Charge to Guest must be zero or greater."],
    [/fixedSessionFullDayFeeAud is required/i,
      "Set the Full-day fee for this Fixed Session card."],
    [/fixedSessionPriceAud is required/i,
      "Set the Charge to Guest amount for this Fixed Session card."],
    [/fixedSessionDurationMinutes cannot exceed the active window duration/i,
      "The Fixed-fee duration cannot be longer than the active window."],
    [/fixedSessionDurationMinutes must be a multiple of 15/i,
      "The Fixed-fee duration must be in 15-minute increments."],
    [/fixedSessionDurationMinutes must be a positive integer/i,
      "Pick a Fixed-fee duration greater than zero."],
    [/fixedSessionDurationMinutes is required/i,
      "Pick the Fixed-fee duration for this Fixed Session card."],
    [/activeFromMinute must be earlier than activeToMinute/i,
      '"Active to time" must be later than "Active from time".'],
    [/activeDaysOfWeek must be a non-empty array/i,
      "Pick at least one active day for this Fixed Session card."],
    [/activeDaysOfWeek values must be ISO weekday numbers/i,
      "Active days must be Mon–Sun."],
    [/Active days, activeFromMinute, and activeToMinute are required/i,
      "Pick active days and set the active from/to times."],
    [/weekendDaysOfWeek/i,
      "Weekend applicable days must be Mon–Sun."],
  ];
  for (const [pattern, friendly] of patterns) {
    if (pattern.test(cleaned)) return friendly;
  }
  return cleaned || "Something went wrong. Please try again.";
}

export function MultiDatePickerPopover({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);

  const dateSet = new Set(value);

  const toggleDate = (m) => {
    if (!moment.isMoment(m)) return;
    const iso = m.format("YYYY-MM-DD");
    const next = new Set(dateSet);
    if (next.has(iso)) next.delete(iso);
    else next.add(iso);
    onChange(Array.from(next).sort());
  };

  const removeDate = (iso) => {
    onChange(value.filter((d) => d !== iso));
  };

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className={css({ position: "relative" })}>
        <Button
          spaceBelow="none"
          flavour="gray"
          size="small"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          {value.length > 0
            ? `${value.length} date${value.length === 1 ? "" : "s"} selected`
            : "Pick dates"}
        </Button>
        {open && (
          <div
            className={css({
              position: "absolute",
              top: "100%",
              left: 0,
              marginTop: 4,
              zIndex: 99,
              background: theme.white,
              borderRadius: 6,
              boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
              " .CalendarMonthGrid": { background: "transparent" },
              " .CalendarDay__highlighted_calendar": {
                background: theme.blue,
                color: theme.white,
                border: `1px double ${theme.blue}`,
              },
              " .CalendarDay__highlighted_calendar:hover": {
                background: theme.blue,
                color: theme.white,
              },
            })}
          >
            <DayPickerSingleDateController
              date={null}
              focused
              onDateChange={toggleDate}
              onFocusChange={() => { }}
              isDayHighlighted={(d) =>
                moment.isMoment(d) && dateSet.has(d.format("YYYY-MM-DD"))
              }
              numberOfMonths={1}
              hideKeyboardShortcutsPanel
              keepOpenOnDateSelect
              noBorder
            />
          </div>
        )}
        <div
          className={css({
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginTop: 8,
          })}
        >
          {value.map((iso) => (
            <span
              key={iso}
              className={css({
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 8px",
                background: theme.gray10,
                borderRadius: 12,
                fontSize: 13,
              })}
            >
              {moment(iso).format("ddd, D MMM YYYY")}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeDate(iso)}
                  className={css({
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: theme.gray70,
                    padding: 0,
                    fontSize: 16,
                    lineHeight: 1,
                  })}
                  aria-label={`Remove ${iso}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      </div>
    </OutsideClickHandler>
  );
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseDynamicPublicUrlEmails(value) {
  if (!value) return [];
  return value
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
}

export function validateDynamicPublicUrlEmails(value) {
  const emails = parseDynamicPublicUrlEmails(value);
  if (!emails.length) return undefined;

  const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email));
  if (!invalidEmails.length) return undefined;

  return `Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`;
}

export function DefaultExitInfo() {
  return (
    <>
      <p>
        The exiting guest card is the guest card used by default for pay-at-gate
        bookings for supported installations.
      </p>
      <p>
        Bookings created in this way still appear in the bookings list as usual
        and can be updated by hotel staff as required.
      </p>
    </>
  );
}

export function PublicConsoleInfo({ url, secondaryUrl }) {
  return (
    <>
      <p>
        The public guest card allows guests to create their own 1-day parking
        bookings through the <a href={url}>public parking console</a>. Guests
        will enter their details and optionally receive a booking confirmation
        and payment receipt by email.
      </p>
      {secondaryUrl && (
        <p>
          Hotel 57 <a href={secondaryUrl}>public parking console</a>.
        </p>
      )}
      <p>
        Bookings created in this way still appear in the bookings list as usual
        and can be updated by hotel staff as required.
      </p>
    </>
  );
}

const ALERT_CSS = css({ paddingTop: 24 });

export function DefaultExitPrompt({ onChange, value, disabled }) {
  if (value) {
    return (
      <Alert flavour="info" className={ALERT_CSS}>
        <strong>
          This guest card will be set as the default Guest Card for guests
          exiting without a booking.
        </strong>
        <DefaultExitInfo />
        <Button
          flavour="danger"
          spaceBelow="none"
          onClick={() => onChange(false)}
          disabled={disabled}
        >
          Unset as the default Exiting Guest Card
        </Button>
      </Alert>
    );
  }

  return (
    <Alert flavour="warning" className={ALERT_CSS}>
      <strong>Change the default Exiting Guest Card</strong>
      <DefaultExitInfo />
      <Button
        spaceBelow="none"
        onClick={() => onChange(true)}
        disabled={disabled}
      >
        Set as the default Exiting Guest Card
      </Button>
    </Alert>
  );
}

export function _DefaultPublicPrompt({
  appConfig,
  installation,
  onChange,
  value,
  disabled,
  rateCard,
}) {
  const publicConsoleUrl = `${appConfig.PUBLIC_CONSOLE_URL}/${installation.publicConsoleSlug}${rateCard?.publicUrl ? `/${rateCard.publicUrl}` : ""}`;
  let hotel57PublicConsoleUrl = null;
  if (installation?.brandingKey === "rydges-sydney-central") {
    hotel57PublicConsoleUrl = `${appConfig.PUBLIC_CONSOLE_URL}/${installation.publicConsoleSlug}${rateCard?.publicUrl ? `/${rateCard.publicUrl}?h57=t` : "?h57=t"}`;
  }

  if (value) {
    return (
      <Alert flavour="info" className={ALERT_CSS}>
        <strong>
          This guest card will be set as the default Public Guest Card.
        </strong>
        <PublicConsoleInfo
          url={publicConsoleUrl}
          secondaryUrl={hotel57PublicConsoleUrl}
        />
        <Button
          flavour="danger"
          spaceBelow="none"
          onClick={() => onChange(false)}
          disabled={disabled}
        >
          Unset as the default Public Guest Card
        </Button>
      </Alert>
    );
  }

  return (
    <Alert flavour="warning" className={ALERT_CSS}>
      <strong>Change the default Public Guest Card</strong>
      <PublicConsoleInfo
        url={publicConsoleUrl}
        secondaryUrl={hotel57PublicConsoleUrl}
      />
      <Button
        spaceBelow="none"
        onClick={() => onChange(true)}
        disabled={disabled}
      >
        Set as the default Public Guest Card
      </Button>
    </Alert>
  );
}

const DefaultPublicPrompt = withAppConfig(_DefaultPublicPrompt);

export function Triple({
  currency,
  serviceFeeRatio,
  type,
  disabled,
  error,
  onChange,
  value,
  isInputAsCurrency = false,
}) {
  const serviceFee = serviceFeeRatio * Number(value);
  const baseRate = Number(value) - serviceFee;

  return (
    <ColContainer columnTemplate={TPL_THIRDS}>
      {isInputAsCurrency ? (
        <CurrencyField
          label="Charge to Guest"
          value={value}
          currency={currency}
        />
      ) : (
        <Input
          required
          label="Charge to Guest"
          type={type}
          disabled={disabled}
          error={error}
          onChange={onChange}
          value={value}
        />
      )}
      <CurrencyField
        label="Service Fee"
        value={serviceFee}
        currency={currency}
      />
      <CurrencyField label="Base Rate" value={baseRate} currency={currency} />
    </ColContainer>
  );
}

const ALIGN_LEFT = css({ textAlign: "left !important" });
const ALIGN_RIGHT = css({ textAlign: "right !important" });
const FLEX_RIGHT = css({ display: "flex", justifyContent: "flex-end", alignItems: "center" });

function FieldRow({ name, ...props }) {
  return (
    <tr>
      <td>
        <span className={ALIGN_LEFT}>{name}</span>
      </td>
      <td className={ALIGN_RIGHT}>
        <CheckboxInput {...props} />
      </td>
    </tr>
  );
}

function TextInputRow({ name, ...props }) {
  return (
    <tr>
      <td>
        <span className={ALIGN_LEFT}>{name}</span>
      </td>
      <td className={ALIGN_RIGHT}>
        <Input {...props} />
      </td>
    </tr>
  );
}

function TextAreaRow({ name, ...props }) {
  return (
    <tr>
      <td>
        <span className={ALIGN_LEFT}>{name}</span>
      </td>
      <td className={ALIGN_RIGHT}>
        <textarea
          className={css({
            display: "block",
            width: "100%",
            background: theme.white,
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: "#EAEDEF",
            borderRadius: "6px",
            fontSize: "18px",
            transition: theme.transitionSnappy,
            ":focus": {
              outline: "none",
              borderColor: theme.blue,
            },
            ":disabled": {
              opacity: 0.5,
            },
            "::placeholder": {
              color: theme.gray20,
            },
          })}
          {...props}
        />
      </td>
    </tr>
  );
}

function ButtonsRow({ title, name, options, register }) {
  return (
    <tr>
      <td>
        <span className={ALIGN_LEFT}>{title}</span>
      </td>
      <td className={FLEX_RIGHT}>
        <div className={css(
          {
            display: 'flex',
            gap: 1,
            backgroundColor: theme.gray15,
            width: 'fit-content',
            borderRadius: 6,
            border: 1,
            borderStyle: 'solid',
            borderColor: theme.gray15,
            overflow: 'hidden',
          }
        )}
        >
          {
            options.map(o => <ButtonInput key={o} {...register(name, o)} label={o} type="radio" value={o} />)
          }
        </div>
      </td>
    </tr>
  )
}

/*
 * The lot a guest card is on.
 *
 * `rateCardById` selects `lot { id name }` and not the scalar `lotId`, so
 * reading `rateCard.lotId` yields undefined. That is why the edit form used to
 * open with no lot selected - and why the submit pinned the old value back on
 * top of the operator's choice to stop every save clearing the lot. Reads the
 * relation first, and falls back to the scalar for any caller that selects it.
 */
export function rateCardLotId(rateCard) {
  return rateCard?.lot?.id ?? rateCard?.lotId ?? null;
}

export default function DetailsForm({
  installation,
  rateCard,
  rateCardStatus,
  onSubmit,
  onCancel,
}) {
  const [{ errors: rawErrors, values, setField }, { text, checkbox, select, raw, number, email, radio }] =
    useFormState({
      name: rateCard ? rateCard.name : "",
      type: rateCard ? rateCard.type : "DAILY",
      calculationMode: rateCard?.calculationMode || "SINGLE_DAY",
      defaultOnExit: rateCard ? rateCard.defaultOnExit : false,
      defaultPublic: rateCard ? rateCard.defaultPublic : false,
      isPublic: rateCard ? rateCard.isPublic : false,
      parklioBayAssignment: rateCard ? rateCard.parklioBayAssignment : false,
      selfPark: rateCard ? rateCard.selfPark : false,
      publicUrl: rateCard ? rateCard.publicUrl : "",
      stopPublicBookingWhenLotFull: rateCard
        ? rateCard.stopPublicBookingWhenLotFull
        : false,
      lotFullText: rateCard ? rateCard.lotFullText : "",
      homeText: rateCard ? rateCard.homeText : "",
      homeTitle: rateCard
        ? rateCard.homeTitle ?? ""
        : "Secure Your Spot Today",

      // Daily
      entryAfterTime: rateCard ? rateCard.entryAfterTime : 600, // 10am
      exitBeforeTime: rateCard ? rateCard.exitBeforeTime : 600, // 10am
      bookingRateAudPerDay: rateCard ? rateCard.bookingRateAudPerDay : "0",
      overstayFlagfallAud: rateCard ? rateCard.overstayFlagfallAud : "0",
      overstayRateAudPerHour: rateCard ? rateCard.overstayRateAudPerHour : "0",
      overstayGraceMinutes: rateCard ? rateCard.overstayGraceMinutes : 15, // 15 minutes
      weekendSurchargeRate: rateCard ? rateCard.weekendSurchargeRate : "0",

      // Hourly
      bookingRateAudPerHour: rateCard ? rateCard.bookingRateAudPerHour : "0",
      chargeOnExpiryAud: rateCard ? rateCard.chargeOnExpiryAud : "0",

      // Mixed
      maximum24HourAud: rateCard ? rateCard.maximum24HourAud : "0",
      hoursUntilExpiry: rateCard ? rateCard.hoursUntilExpiry : 120, // 5 days

      // Guest fields
      guestName: rateCard ? rateCard.guestName : true,
      guestEmail: rateCard ? rateCard.guestEmail : false,
      guestCompany: rateCard ? rateCard.guestCompany : false,
      guestDepartment: rateCard ? rateCard.guestDepartment : false,
      guestPhone: rateCard ? rateCard.guestPhone : false,
      guestRoom: rateCard ? rateCard.guestRoom : false,
      guestApartmentNumber: rateCard ? rateCard.guestApartmentNumber : false,
      guestReservation: rateCard ? rateCard.guestReservation : false,
      guestEmployeeCode: rateCard ? rateCard.guestEmployeeCode : false,
      guestPosition: rateCard ? rateCard.guestPosition : false,
      paymentGate: rateCard ? rateCard.paymentGate : false,
      multiDates: rateCard ? rateCard.multiDates : false,
      paymentStripe: rateCard ? rateCard.paymentStripe : true,
      valetPayLater: rateCard ? rateCard.valetPayLater : false,
      valetRequestFeeAud: rateCard?.valetRequestFeeAud ?? "0",
      valetFreeRequestsPerDay: rateCard?.valetFreeRequestsPerDay ?? 0,
      startsAt: rateCard ? rateCard.startsAt : null,
      expiresAt: rateCard ? rateCard.expiresAt : null,
      bookingsLimit: rateCard ? rateCard.bookingsLimit : null,
      capacity: rateCard ? rateCard.capacity : null,
      lotId: rateCardLotId(rateCard),
      rateCardExtras:
        rateCard && rateCard.rateCardExtras ? rateCard.rateCardExtras : [],
      includeCarType: rateCard ? rateCard.includeCarType : false,
      includeCarNotes: rateCard ? rateCard.includeCarNotes : false,
      ignoreGlobalCapacity: rateCard ? rateCard.ignoreGlobalCapacity : false,
      isValet: rateCard ? rateCard.isValet : false,
      resetLimit: rateCard ? rateCard.resetLimit : 'OFF',

      isVip: rateCard ? rateCard.isVip : false,
      vipEmailRecipient: rateCard ? rateCard.vipEmailRecipient : "",
      defaultVipContactId: rateCard?.defaultVipContactId ?? "",
      hasValetOption: rateCard ? rateCard.hasValetOption : false,
      valetGuestCardLineageId: rateCard?.valetGuestCardLineageId ?? null,
      allowFutureDate: rateCard ? rateCard.allowFutureDate : true,

      // Monthly
      bookingRateAudPerMonth: rateCard ? rateCard.bookingRateAudPerMonth : "0",

      // Tiered Hours fields
      hoursTiers: rateCard?.hoursTiers ?? [],

      // Dynamic Public URL
      isDynamicPublicUrl: rateCard?.isDynamicPublicUrl || false,
      dynamicPublicUrlUpdateHour: rateCard?.dynamicPublicUrlUpdateHour ?? 0,
      dynamicPublicUrlEmails: jsonValueSafe(rateCard?.dynamicPublicUrlEmails, []).join(','),

      // FIXED_SESSION fields
      activeFromMinute: rateCard?.activeFromMinute ?? 480,           // 08:00 (8 AM)
      activeToMinute: rateCard?.activeToMinute ?? 1020,              // 17:00 (5 PM)
      fixedSessionPriceAud: rateCard?.fixedSessionPriceAud ?? "0",
      fixedSessionDurationMinutes: rateCard?.fixedSessionDurationMinutes ?? 180,
      fixedSessionFullDayFeeAud: rateCard?.fixedSessionFullDayFeeAud ?? "0",

      // MULTI_DATE_FIXED_PACKAGE fields
      fixedBookingStartDate: rateCard?.fixedBookingStartDate ?? null,
      fixedBookingEndDate: rateCard?.fixedBookingEndDate ?? null,
      packagePriceAud: rateCard?.packagePriceAud ?? "0",
    });

  const errors = useMemo(() => {
    const result = {}
    Object.keys(rawErrors).forEach((key) => {
      if (rawErrors[key] === undefined) return
      result[key] = rawErrors[key]
    })
    return result
  }, [rawErrors]);

  /*
   * FIXED_SESSION: activeDaysOfWeek lives outside useFormState because it's
   * a multi-select array (Mon..Sun) — react-use-form-state doesn't support
   * array-valued multi-checkbox groups out of the box.
   */
  const [activeDaysOfWeek, setActiveDaysOfWeek] = useState(() => {
    if (rateCard?.activeDaysOfWeek) {
      // jsonb may come back as array OR JSON string depending on hydration path
      try {
        return Array.isArray(rateCard.activeDaysOfWeek)
          ? rateCard.activeDaysOfWeek
          : JSON.parse(rateCard.activeDaysOfWeek);
      } catch {
        return [];
      }
    }
    return [1, 2, 3, 4, 5]; // Mon-Fri default
  });
  const toggleActiveDay = (day) => {
    setActiveDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
    );
  };

  /*
   * Days the weekend surcharge applies to. Empty means "use the built-in
   * default" — Fri/Sat nights for overnight cards, Sat/Sun for the rest.
   */
  const [weekendDaysOfWeek, setWeekendDaysOfWeek] = useState(() => {
    if (rateCard?.weekendDaysOfWeek) {
      /* jsonb may come back as array OR JSON string depending on hydration path */
      try {
        const parsed = Array.isArray(rateCard.weekendDaysOfWeek)
          ? rateCard.weekendDaysOfWeek
          : JSON.parse(rateCard.weekendDaysOfWeek);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  });
  const toggleWeekendDay = (day) => {
    setWeekendDaysOfWeek((prev) =>
      prev.includes(day)
        ? prev.filter((d) => d !== day)
        : [...prev, day].sort((a, b) => a - b),
    );
  };

  if (values.type === "DAILY") {
    if (values.multiDates && values.calculationMode.includes("SINGLE")) {
      values.calculationMode = "MULTI_DATE_OVERNIGHT";
    }

    if (!values.multiDates && values.calculationMode.includes("MULTI")) {
      values.calculationMode = "SINGLE_DAY";
    }
  }

  if (["MONTHLY", "HOURLY", "TIERED_HOURS"].includes(values.type) && !rateCard) {
    values.entryAfterTime = 0; // 12am
    values.exitBeforeTime = 1425; // 11:45pm
  }

  const [createRateCard, { error: submitError }] = useMutation(createRateCardMutation);
  const [getExtraImageUploadUrl] = useMutation(extraImageUploadUrlMutation);

  const {
    data: lots,
    loading: lotsLoading,
    error,
  } = useQuery(allLots, {
    variables: {
      installationId: installation.id,
    },
  });

  const { facility } = useInstallation() ?? {};
  const vipRoutingEnabled = isConciergePageEnabled(facility, "vipRouting");

  const { data: contactsData } = useQuery(bookingContactsQuery, {
    variables: { installationId: installation.id },
    skip: !vipRoutingEnabled,
  });
  const vipContacts = contactsData?.contacts ?? [];

  /* Landing title only applies to the new booking page, so the field only
     shows for hotels with the v2 feature on. */
  const { data: uiV2Data } = useQuery(bookingUiV2EnabledQuery, {
    variables: { installationId: installation.id },
  });
  const bookingUiV2Enabled = !!uiV2Data?.bookingUiV2Enabled;

  const { data: allRateCardsData } = useQuery(allRateCards, {
    variables: {
      installationId: installation.id,
      isArchived: false,
    },
  });

  const valetTargetOptions = (() => {
    const cards = allRateCardsData?.allRateCards ?? [];
    const latestByLineage = new Map();
    for (const card of cards) {
      if (!card?.lineageId) continue;
      if (rateCard?.lineageId && card.lineageId === rateCard.lineageId) continue;
      if (card.isValet !== true) continue;
      const existing = latestByLineage.get(card.lineageId);
      if (!existing || (card.version ?? 0) > (existing.version ?? 0)) {
        latestByLineage.set(card.lineageId, card);
      }
    }
    return Array.from(latestByLineage.values())
      .map((card) => ({ value: card.lineageId, label: card.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const [rateCardExtras, setRateCardExtras] = useState(
    rateCard && rateCard.rateCardExtras ? rateCard.rateCardExtras : [],
  );
  const [hoursTiers, setHoursTiers] = useState(() => {
    return structuredClone(rateCard?.hoursTiers ?? []);
  });
  const [customHourlyRates, setCustomHourlyRates] = useState(() => {
    if (rateCard?.customHourlyRates) {
      try {
        return typeof rateCard.customHourlyRates === "string"
          ? JSON.parse(rateCard.customHourlyRates)
          : rateCard.customHourlyRates;
      } catch (err) {
        console.error("Failed to parse customHourlyRates", err);
      }
    }
    return [];
  });
  const [showCustomHourlyRates, setShowCustomHourlyRates] = useState(
    () => Array.isArray(customHourlyRates) && customHourlyRates.length > 0,
  );
  const [customDayRateRows, setCustomDayRateRows] = useState(() =>
    groupCustomDayRatesByRate(rateCard?.customDayRates),
  );
  const lotsOptions = lots ? lots.allLots : [];

  const duplicateUpToHour = (() => {
    const seen = new Set();
    for (const tier of customHourlyRates) {
      const h = Number(tier.upToHour);
      if (seen.has(h)) return h;
      seen.add(h);
    }
    return null;
  })();

  const misalignedUpToHour = (() => {
    for (const tier of customHourlyRates) {
      const h = Number(tier.upToHour);
      if (!Number.isFinite(h) || h <= 0) return h;
      if (Math.abs(h * 2 - Math.round(h * 2)) > 1e-9) return h;
    }
    return null;
  })();

  function formatHourLabel(h) {
    const n = Number(h);
    if (!Number.isFinite(n)) return String(h);
    if (Number.isInteger(n)) return `${n}h`;
    const whole = Math.floor(n);
    const minutes = Math.round((n - whole) * 60);
    if (whole === 0) return `${minutes}m`;
    return `${whole}h ${minutes}m`;
  }

  async function _onSubmit() {
    values.defaultVipContactId = values.defaultVipContactId || null;
    values.entryAfterTime = parseInt(values.entryAfterTime);
    values.exitBeforeTime = parseInt(values.exitBeforeTime);
    values.overstayGraceMinutes = parseInt(values.overstayGraceMinutes);
    values.valetFreeRequestsPerDay =
      parseInt(values.valetFreeRequestsPerDay) || 0;
    values.hoursUntilExpiry = parseInt(values.hoursUntilExpiry);
    values.bookingsLimit = parseInt(values.bookingsLimit);
    values.capacity = parseInt(values.capacity);
    if (values.type === "TIERED_HOURS" || values.type === "MONTHLY")
      values.calculationMode = "LEGACY";
    if (values.type === "HOURLY") {
      values.calculationMode =
        customHourlyRates.length > 0 ? "CUSTOM" : "LEGACY";
      values.customHourlyRates = customHourlyRates;
    }
    if (values.type === "FIXED_SESSION") {
      values.calculationMode = "LEGACY";
      // drivo/api's RateCardCreateInput expects [Int!] for activeDaysOfWeek — send the array directly
      values.activeDaysOfWeek = activeDaysOfWeek;
      values.activeFromMinute = parseInt(values.activeFromMinute);
      values.activeToMinute = parseInt(values.activeToMinute);
      values.fixedSessionDurationMinutes =
        parseInt(values.fixedSessionDurationMinutes, 10) || null;
    } else {
      // For non-FIXED_SESSION cards, do not send the schedule/price fields.
      values.activeDaysOfWeek = null;
      values.activeFromMinute = null;
      values.activeToMinute = null;
      values.fixedSessionPriceAud = null;
      values.fixedSessionDurationMinutes = null;
      values.fixedSessionFullDayFeeAud = null;
    }

    /* Empty selection means "use the default weekend days", stored as null */
    values.weekendDaysOfWeek = weekendDaysOfWeek.length
      ? weekendDaysOfWeek
      : null;

    const isFixedPackageSubmit =
      values.type === "DAILY" &&
      values.calculationMode === "MULTI_DATE_FIXED_PACKAGE";
    if (!isFixedPackageSubmit) {
      values.fixedBookingStartDate = null;
      values.fixedBookingEndDate = null;
      values.packagePriceAud = null;
    } else {
      values.packagePriceAud = String(values.packagePriceAud ?? "0");
      // Mirror the rate card validity window onto the package window so the
      // guest card is only bookable during the configured package dates.
      values.startsAt = values.fixedBookingStartDate;
      values.expiresAt = values.fixedBookingEndDate;
    }

    values.dynamicPublicUrlUpdateHour = parseInt(values.dynamicPublicUrlUpdateHour) || 0;

    /*
     * Upload any newly-selected extra images to S3 on submit (not on file
     * pick), then persist only the returned public URL. A failed upload aborts
     * the save so we never store a broken image reference.
     */
    let uploadedExtras;
    try {
      uploadedExtras = await Promise.all(
        rateCardExtras.map(async (rce) => {
          if (!rce._pendingFile) return rce;
          const { data: uploadData } = await getExtraImageUploadUrl({
            variables: {
              brandingKey: installation?.brandingKey || "",
              contentType: rce._pendingFile.type,
            },
          });
          const target = uploadData && uploadData.extraImageUploadUrl;
          if (!target) throw new Error("No upload URL returned");
          const res = await fetch(target.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": rce._pendingFile.type },
            body: rce._pendingFile,
          });
          if (!res.ok) throw new Error(`Image upload failed (${res.status})`);
          return { ...rce, image: target.publicUrl };
        })
      );
    } catch (err) {
      window.alert("Image upload failed. Please try again.");
      return;
    }

    const variables = {
      installationId: installation.id,
      ...values,
      ...(values.isValet
        ? { hasValetOption: false, valetGuestCardLineageId: null }
        : {}),
      valetPayLater: canPayLater ? values.valetPayLater === true : false,
      valetRequestFeeAud: values.isValet ? values.valetRequestFeeAud : "0",
      valetFreeRequestsPerDay: values.isValet
        ? values.valetFreeRequestsPerDay
        : 0,
      /* homeTitle is only editable when the v2 booking page is on for this
       * hotel. Otherwise preserve whatever the card already has (null for a
       * new card) so v1 saves never rewrite a column the operator can't see. */
      homeTitle: bookingUiV2Enabled
        ? values.homeTitle
        : (rateCard?.homeTitle ?? null),
      customHourlyRates: values.customHourlyRates,
      dynamicPublicUrlEmails: parseDynamicPublicUrlEmails(values.dynamicPublicUrlEmails),
      rateCardExtras: uploadedExtras.map(({ __typename, amount, _pendingFile, _previewUrl, ...rce }) => ({
        ...rce,
        amount: amount.toString(),
      })),
      ...(isMultiDateMode && {
        customDayRates: flattenCustomDayRatesRows(customDayRateRows),
      })
    };

    if (rateCard) {
      /* The lot is deliberately not pinned here. Editing a card writes a new
       * version of its lineage, so the lot can move with it - and `...values`
       * already carries whatever the operator picked. Pinning it back was what
       * made the Lot select look editable while silently discarding the
       * change. */
      Object.assign(variables, { lineageId: rateCard.lineageId });
    }

    if (values.type === "TIERED_HOURS" || (values.type === "DAILY" && hoursTiers.length > 0)) {
      Object.assign(
        variables,
        {
          hoursTiers: hoursTiers
            .map(ht => ({
              rate: parseFloat(ht.rate),
              maxHours: parseInt(ht.maxHours),
            }))
            .sort((a, b) => a.maxHours - b.maxHours),
        }
      );
    }

    /*
     * Apollo's useMutation already surfaces server errors via the `error`
     * (submitError) state we render at the top of the form. Wrap ONLY the
     * mutation call so the rejection doesn't bubble to React's runtime-error
     * overlay — onSubmit() and any other non-mutation code keeps its normal
     * error behaviour, so genuine bugs aren't hidden.
     */
    let data;
    try {
      ({ data } = await createRateCard({ variables }));
    } catch {
      // submitError is already populated by useMutation; the useEffect that
      // watches hasAnyError will scroll the form to the error message.
      return;
    }
    onSubmit(data.createRateCard);
  }

  const isCreating = !rateCard;
  const hasErrors = Object.values(errors).some((x) => x);
  const noEditing = rateCard && rateCard.isArchived;

  /* Rendered in both modes, so it is built once here rather than kept in step
   * by hand in two branches. "Default" is a real choice, stored as null. */
  const lotField = !lotsLoading && (
    <SelectInput
      label="Lot"
      options={[
        { label: "Default", value: null },
        ...lotsOptions.map((l) => ({
          value: l.id,
          label: `${l.name} (${l.description})`,
        })),
      ]}
      error={errors.lotId}
      {...select("lotId")}
      disabled={noEditing}
    />
  );

  const duplicateCustomDayRateDate = findDuplicateCustomDayRateDate(customDayRateRows);
  const customDayRatesHasInvalidRow = customDayRateRows.some(
    (r) =>
      r.dates.length === 0 ||
      r.dates.some((d) => !ISO_DATE_REGEX.test(d)) ||
      !Number.isFinite(Number(r.rate)) ||
      Number(r.rate) < 0,
  );

  const { serviceFeePercent } = rateCard || installation;
  const isDaily = values.type === "DAILY";
  const isMonthly = values.type === "MONTHLY";
  const isTieredHours = values.type === "TIERED_HOURS";
  const isFixedSession = values.type === "FIXED_SESSION";
  const isFixedPackage =
    values.type === "DAILY" &&
    values.calculationMode === "MULTI_DATE_FIXED_PACKAGE";
  const optionals = determineOptionals(values);
  const { currency } = installation;

  const fixedSessionDayMissing = isFixedSession && activeDaysOfWeek.length === 0;
  const fixedSessionTimesInvalid =
    isFixedSession &&
    Number(values.activeFromMinute) >= Number(values.activeToMinute);

  /**
   * Fixed-fee duration dropdown options: 15-min steps up to the active-window
   * length. Label format is human-readable, e.g. "15 minutes", "1 hour",
   * "2 hours 30 minutes". Empty when the active window is not yet valid.
   */
  const fixedSessionWindowMinutes =
    Number(values.activeToMinute) - Number(values.activeFromMinute);
  const formatDurationLabel = (m) => {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    const parts = [];
    if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
    if (rem > 0) parts.push(`${rem} ${rem === 1 ? "minute" : "minutes"}`);
    return parts.join(" ");
  };
  const fixedSessionDurationOptions = (() => {
    if (!isFixedSession) return [];
    if (!Number.isFinite(fixedSessionWindowMinutes) || fixedSessionWindowMinutes <= 0)
      return [];
    const opts = [];
    for (let m = 15; m <= fixedSessionWindowMinutes; m += 15) {
      opts.push({ value: m, label: formatDurationLabel(m) });
    }
    return opts;
  })();

  /*
   * Reset the duration only when the active window is VALID but the picked
   * value genuinely no longer fits. Transient invalid windows (from >= to
   * mid-edit) are left alone so the selection survives an in-progress edit.
   */
  useEffect(() => {
    if (!isFixedSession) return;
    const current = parseInt(values.fixedSessionDurationMinutes, 10);
    if (
      current &&
      Number.isFinite(fixedSessionWindowMinutes) &&
      fixedSessionWindowMinutes > 0 &&
      current > fixedSessionWindowMinutes
    ) {
      setField("fixedSessionDurationMinutes", "");
    }
  }, [isFixedSession, fixedSessionWindowMinutes]);

  const fixedSessionDurationMissing =
    isFixedSession && !parseInt(values.fixedSessionDurationMinutes, 10);

  const fixedPackageDatesMissing =
    isFixedPackage &&
    (!values.fixedBookingStartDate || !values.fixedBookingEndDate);
  const fixedPackageDatesInvalid =
    isFixedPackage &&
    !!values.fixedBookingStartDate &&
    !!values.fixedBookingEndDate &&
    (!ISO_DATE_REGEX.test(String(values.fixedBookingStartDate)) ||
      !ISO_DATE_REGEX.test(String(values.fixedBookingEndDate)) ||
      String(values.fixedBookingStartDate) >
      String(values.fixedBookingEndDate));
  const fixedPackagePriceInvalid =
    isFixedPackage &&
    (!Number.isFinite(Number(values.packagePriceAud)) ||
      Number(values.packagePriceAud) < 0);

  const isCardItselfValet = values.isValet === true;

  const canPayLater =
    isCardItselfValet && values.paymentStripe === true;

  const isValetCheckboxProps = checkbox("isValet");
  const onIsValetChange = (event) => {
    isValetCheckboxProps.onChange(event);

    if (event.target.checked) {
      setField("ignoreGlobalCapacity", true);
      return;
    }

    setField("valetPayLater", false);
  };

  const valetGuestCardMissing =
    !isCardItselfValet &&
    values.hasValetOption === true &&
    !values.valetGuestCardLineageId;

  const canSubmit =
    !hasErrors &&
    !noEditing &&
    duplicateUpToHour === null &&
    misalignedUpToHour === null &&
    !fixedSessionDayMissing &&
    !fixedSessionTimesInvalid &&
    !fixedSessionDurationMissing &&
    duplicateCustomDayRateDate === null &&
    !customDayRatesHasInvalidRow &&
    !fixedPackageDatesMissing &&
    !fixedPackageDatesInvalid &&
    !fixedPackagePriceInvalid &&
    !valetGuestCardMissing;

  // Tier editor functions for TIERED_HOURS
  const addTier = () => {
    const lastTier = hoursTiers[hoursTiers.length - 1];
    const nextMaxHours = lastTier ? lastTier.maxHours + 4 : 4;
    setHoursTiers([...hoursTiers, { maxHours: nextMaxHours, rate: 0 }]);
  };

  const removeTier = (index) => {
    setHoursTiers(hoursTiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const updated = [...hoursTiers];
    updated[index] = { ...updated[index], [field]: value };
    setHoursTiers(updated);
  };

  const sortTiers = (tiers) =>
    [...tiers].sort((a, b) => Number(a.upToHour) - Number(b.upToHour));

  const addCustomHourlyRate = () => {
    const lastTier = customHourlyRates[customHourlyRates.length - 1];
    const nextUpToHour = lastTier ? Number(lastTier.upToHour) + 0.5 : 0.5;
    setCustomHourlyRates(
      sortTiers([
        ...customHourlyRates,
        { upToHour: nextUpToHour, rate: values.bookingRateAudPerHour },
      ]),
    );
  };

  const removeCustomHourlyRate = (index) => {
    setCustomHourlyRates(customHourlyRates.filter((_, i) => i !== index));
  };

  const updateCustomHourlyRate = (index, field, value) => {
    const updated = [...customHourlyRates];
    updated[index] = { ...updated[index], [field]: value };
    setCustomHourlyRates(sortTiers(updated));
  };

  const isMultiDateMode =
    values.type === "DAILY" &&
    (values.calculationMode === "MULTI_DATE_OVERNIGHT" ||
      values.calculationMode === "MULTI_DATE_DAYTIME");

  /*
   * Mirrors getWeekendDays() in drivo/api — what applies when no days are
   * picked. Rydges World Square keeps calendar-day weekends even overnight.
   */
  const RYDGES_WORLD_SQUARE_INSTALLATION_ID =
    "ca4bd481-39ef-4c59-ab1c-4c36eb797279";
  const defaultWeekendDaysLabel =
    values.calculationMode === "MULTI_DATE_OVERNIGHT" &&
    installation.id !== RYDGES_WORLD_SQUARE_INSTALLATION_ID
      ? "Friday and Saturday nights"
      : "Saturday and Sunday";

  useEffect(() => {
    if (!isMultiDateMode && customDayRateRows.length > 0) {
      setCustomDayRateRows([]);
    }
  }, [isMultiDateMode]);

  const addCustomDayRateRow = () => {
    setCustomDayRateRows((rows) => [
      ...rows,
      { id: nextCdrRowId(), dates: [], rate: values.bookingRateAudPerDay || "0" },
    ]);
  };

  const removeCustomDayRateRow = (id) => {
    setCustomDayRateRows((rows) => rows.filter((r) => r.id !== id));
  };

  const updateCustomDayRateRow = (id, patch) => {
    setCustomDayRateRows((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );
  };

  const dataRows = [
    { name: "Guest Name", key: "guestName" },
    { name: "Guest Email", key: "guestEmail" },
    { name: "Guest Phone", key: "guestPhone" },
    { name: "Guest Company", key: "guestCompany" },
    { name: "Guest Department", key: "guestDepartment" },
    { name: "Guest Room", key: "guestRoom" },
    { name: "Resident Apartment Number", key: "guestApartmentNumber" },
    { name: "Guest Reservation", key: "guestReservation" },
    { name: "Employee Code", key: "guestEmployeeCode" },
    { name: "Position", key: "guestPosition" },
    { name: "Gate Payment", key: "paymentGate" },
    { name: "Stripe Payment", key: "paymentStripe" },
  ].map(({ name, key }) => {
    const disabled = optionals[key] === false;
    return (
      <FieldRow
        key={key}
        {...checkbox(key)}
        name={name}
        disabled={noEditing || disabled}
      />
    );
  });

  const serviceFeeRatio = Number(serviceFeePercent) / 100;

  const [qrcode, setQrcode] = useState(false);
  const [isHotel57, setIsHotel57] = useState(false);
  const componentRef = useRef();

  /*
   * Scroll the form back to the top whenever any error becomes visible —
   * server error, field-validation error, or one of the FIXED_SESSION /
   * FIXED_PACKAGE inline errors. Mirrors the Booking/Create.js pattern so
   * the operator always sees the error rather than landing on a disabled
   * submit button at the bottom.
   */
  const errorAnchorRef = useRef(null);
  const hasAnyError =
    !!submitError ||
    hasErrors ||
    fixedSessionTimesInvalid ||
    fixedSessionDayMissing ||
    fixedSessionDurationMissing ||
    fixedPackageDatesMissing;
  useEffect(() => {
    if (hasAnyError && errorAnchorRef.current) {
      errorAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasAnyError]);

  async function handlePrintFn(handlePrint, hotel57 = false) {
    if (hotel57) await setIsHotel57(true);
    await setQrcode(true);
    handlePrint();
  }

  return (
    <div>
      <span ref={errorAnchorRef} />
      {submitError && <LoadingError error={toFriendlyRateCardError(submitError)} />}
      {hasErrors && <LoadingError error={Object.values(errors)[0]} />}
      {fixedSessionTimesInvalid && (
        <LoadingError error={'"Active to time" must be later than "Active from time".'} />
      )}
      {fixedSessionDayMissing && (
        <LoadingError error={"Pick at least one active day for the Fixed Session card."} />
      )}
      {fixedSessionDurationMissing && !fixedSessionTimesInvalid && (
        <LoadingError error={"Select the fixed-fee duration for the Fixed Session card."} />
      )}
      {fixedPackageDatesMissing && (
        <LoadingError error={"Set both Package Start Date and Package End Date for the Fixed Package card."} />
      )}

      {/* Shown in both modes now that an edit has a field under it. */}
      <H3>Guest Card Details</H3>
      {/*
        * Label and Type stay creation-only on purpose: saving an edit writes a
        * new version of the lineage, and the API takes both from the parent
        * version, so offering them here would ignore whatever was typed. The
        * lot is not inherited - it is persisted straight off the input - so it
        * is offered in both modes.
        */}
      {isCreating ? (
        <ColContainer columnTemplate={TPL_THIRDS}>
          <Input
            label="Label"
            {...text({
              name: "name",
              validate: (value) => {
                if (!value) return "Label is required";
                if (
                  !authManager.currentUser.isRootAdmin &&
                  value.toLowerCase().includes("ohip")
                ) {
                  return "You are not allowed to create OHIP rate cards";
                }
                return "";
              },
            })}
            required
          />
          <SelectInput
            label="Type"
            options={enums.RATECARD_TYPE_OPTIONS}
            error={errors.type}
            {...select("type")}
            disabled={noEditing}
          />
          {lotField}
        </ColContainer>
      ) : (
        <ColContainer columnTemplate={TPL_THIRDS}>{lotField}</ColContainer>
      )}
      <HR spaceAbove="medium" spaceBelow="medium" />

      {isTieredHours ? (
        <>
          <H3>Tiered Hours Pricing</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            Configure pricing tiers based on parking duration. Guests are
            charged the flat rate for the tier their parking duration falls
            within.
          </p>
          <Table>
            <THead>
              <tr>
                <th className={ALIGN_LEFT}>Up to (hours)</th>
                <th className={ALIGN_LEFT}>Rate ({currency})</th>
                <th className={ALIGN_RIGHT}>Actions</th>
              </tr>
            </THead>
            <TBody>
              {hoursTiers.map((tier, index) => (
                <tr key={index}>
                  <td>
                    <Input
                      type="number"
                      value={tier.maxHours}
                      onChange={(e) =>
                        updateTier(
                          index,
                          "maxHours",
                          e.target.value,
                        )
                      }
                      disabled={noEditing}
                      style={{ width: 100 }}
                    />
                  </td>
                  <td>
                    <Input
                      type="text"
                      value={tier.rate}
                      onChange={(e) =>
                        updateTier(index, "rate", e.target.value)
                      }
                      disabled={noEditing}
                      style={{ width: 120 }}
                    />
                  </td>
                  <td className={ALIGN_RIGHT}>
                    <Button
                      flavour="danger"
                      spaceBelow="none"
                      onClick={() => removeTier(index)}
                      disabled={noEditing}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </TBody>
          </Table>
          <Button
            spaceBelow="medium"
            onClick={addTier}
            disabled={noEditing}
            style={{ marginTop: 16 }}
          >
            Add Tier
          </Button>

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay rate (per hour, beyond all tiers)</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.overstayRateAudPerHour}
            {...text({
              name: "overstayRateAudPerHour",
              validate: validateAUD,
            })}
            disabled={noEditing}
          />
        </>
      ) : isMonthly ? (
        <>
          <H3>Monthly rate (per month)</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.bookingRateAudPerMonth}
            {...text({ name: "bookingRateAudPerMonth", validate: validateAUD })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay flagfall (flat fee)</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.overstayFlagfallAud}
            {...text({ name: "overstayFlagfallAud", validate: validateAUD })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay rate (per hour)</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.overstayRateAudPerHour}
            {...text({
              name: "overstayRateAudPerHour",
              validate: validateAUD,
            })}
            disabled={noEditing}
          />
        </>
      ) : isDaily && rateCard?.calculationMode !== "LEGACY" ? (
        <>
          {isFixedPackage ? (
            <>
              <H3>Package Dates</H3>
              <p style={{ marginBottom: 16, color: "#666" }}>
                The booking dates are fixed for everyone using this guest card.
                Entry and exit times are taken from the Schedules section below.
              </p>
              <ColContainer columnTemplate={TPL_THIRDS}>
                <DateInput
                  floatPicker
                  label="Package Start Date"
                  placeholder="Enter start date"
                  autoComplete="off"
                  displayFormat="ddd, D MMM YYYY"
                  isOutsideRange={() => false}
                  type="text"
                  disabled={noEditing}
                  {...raw({ name: "fixedBookingStartDate" })}
                />
                <DateInput
                  floatPicker
                  label="Package End Date"
                  placeholder="Enter end date"
                  autoComplete="off"
                  displayFormat="ddd, D MMM YYYY"
                  isOutsideRange={() => false}
                  type="text"
                  disabled={noEditing}
                  {...raw({ name: "fixedBookingEndDate" })}
                />
              </ColContainer>
              {fixedPackageDatesInvalid && (
                <Alert flavour="danger" spaceAbove="xsmall" spaceBelow="none">
                  Package start date must be on or before the end date.
                </Alert>
              )}

              <HR spaceAbove="medium" spaceBelow="medium" />
              <H3>Package Price (flat total)</H3>
              <p style={{ marginBottom: 16, color: "#666" }}>
                Guests are charged this single flat amount for the whole
                package, regardless of the number of nights or weekend days.
              </p>
              <Triple
                currency={currency}
                serviceFeeRatio={serviceFeeRatio}
                error={errors.packagePriceAud}
                {...text({
                  name: "packagePriceAud",
                  validate: validateAUD,
                })}
                disabled={noEditing}
              />
              {fixedPackagePriceInvalid && (
                <Alert flavour="danger" spaceAbove="xsmall" spaceBelow="none">
                  Package price must be a non-negative number.
                </Alert>
              )}
            </>
          ) : (
            <>
              <H3>Daily rate (per day)</H3>
              <Triple
                currency={currency}
                serviceFeeRatio={serviceFeeRatio}
                error={errors.bookingRateAudPerDay}
                {...text({ name: "bookingRateAudPerDay", validate: validateAUD })}
                disabled={noEditing}
              />

              <HR spaceAbove="medium" spaceBelow="medium" />
              <H3>Weekend rate (per day)</H3>
              <ColContainer columnTemplate={TPL_THIRDS}>
                <Input
                  required
                  label="Surcharge (additional to daily rate)"
                  disabled={noEditing}
                  {...text({ name: "weekendSurchargeRate", validate: validateAUD })}
                  error={errors.weekendSurchargeRate}
                />
              </ColContainer>
              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <Label>Applicable Days</Label>
                <p style={{ margin: "4px 0 8px", color: "#666" }}>
                  Pick the days the surcharge applies to. Leave all unselected to
                  use the default ({defaultWeekendDaysLabel}).
                  {values.calculationMode === "MULTI_DATE_OVERNIGHT" &&
                    " Each day means the check-in night."}
                </p>
                <div
                  className={css({
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginTop: 6,
                  })}
                >
                  {enums.ISO_DAYS_OF_WEEK.map((d) => {
                    const active = weekendDaysOfWeek.includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => !noEditing && toggleWeekendDay(d.value)}
                        disabled={noEditing}
                        aria-pressed={active}
                        className={css({
                          padding: "8px 16px",
                          borderRadius: 6,
                          border: `2px solid ${active ? theme.blue : "#EAEDEF"}`,
                          backgroundColor: active ? theme.blue : "#fff",
                          color: active ? "#fff" : "#333",
                          fontWeight: active ? 600 : 400,
                          cursor: noEditing ? "not-allowed" : "pointer",
                          opacity: noEditing ? 0.5 : 1,
                          minWidth: 60,
                          transition: theme.transitionSnappy,
                        })}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Triple
                currency={currency}
                serviceFeeRatio={serviceFeeRatio}
                disabled={noEditing}
                value={+values.weekendSurchargeRate + +values.bookingRateAudPerDay}
                isInputAsCurrency
              />
            </>
          )}

          {isMultiDateMode && (
            <>
              <HR spaceAbove="medium" spaceBelow="medium" />
              <H3>Custom Day Rates</H3>
              <p style={{ marginBottom: 16, color: "#666" }}>
                Optionally set per-date rates that override the daily rate and
                weekend surcharge for the selected calendar date
                {values.calculationMode === "MULTI_DATE_OVERNIGHT"
                  ? "s (use the check-in date for each night)."
                  : "s."}
              </p>
              {customDayRateRows.map((row) => (
                <div
                  key={row.id}
                  className={css({
                    border: `1px solid ${theme.gray10}`,
                    borderRadius: 6,
                    padding: 16,
                    marginBottom: 12,
                  })}
                >
                  <ColContainer columnTemplate={TPL_THIRDS}>
                    <div>
                      <Label>Dates</Label>
                      <MultiDatePickerPopover
                        value={row.dates}
                        onChange={(dates) =>
                          updateCustomDayRateRow(row.id, { dates })
                        }
                        disabled={noEditing}
                      />
                    </div>
                  </ColContainer>
                  <Triple
                    currency={currency}
                    serviceFeeRatio={serviceFeeRatio}
                    disabled={noEditing}
                    onChange={(e) =>
                      updateCustomDayRateRow(row.id, { rate: e.target.value })
                    }
                    value={row.rate}
                    type="text"
                  />
                  <Button
                    flavour="danger"
                    spaceBelow="none"
                    onClick={() => removeCustomDayRateRow(row.id)}
                    disabled={noEditing}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {duplicateCustomDayRateDate && (
                <Alert flavour="danger" spaceAbove="xsmall" spaceBelow="none">
                  Date {duplicateCustomDayRateDate} appears in more than one
                  custom day rate row. Each date can only have a single rate.
                </Alert>
              )}
              {customDayRatesHasInvalidRow && (
                <Alert flavour="danger" spaceAbove="xsmall" spaceBelow="none">
                  Each custom day rate row must have at least one date and a
                  non-negative rate.
                </Alert>
              )}
              <Button
                spaceBelow="medium"
                onClick={addCustomDayRateRow}
                disabled={noEditing}
                style={{ marginTop: 8 }}
              >
                Add Day Rate
              </Button>
            </>
          )}

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay flagfall (flat fee)</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.overstayFlagfallAud}
            {...text({ name: "overstayFlagfallAud", validate: validateAUD })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <CheckboxInput
              checked={hoursTiers.length > 0}
              onChange={(e) => { if (!e.target.checked) { setHoursTiers([]); } else { addTier(); } }}
              disabled={noEditing}
              id="overstayTieredCheckbox"
            />
            <label htmlFor="overstayTieredCheckbox" style={{ cursor: noEditing ? "not-allowed" : "pointer", fontWeight: 500 }}>
              Use tiered overstay rates
            </label>
          </div>

          {hoursTiers.length > 0 ? (
            <>
              <p style={{ marginBottom: 16, color: "#666" }}>
                Guests are charged the flat rate for the tier their overstay duration falls within. Beyond all tiers, the per-hour rate below is used.
              </p>
              <Table>
                <THead>
                  <tr>
                    <th className={ALIGN_LEFT}>Up to (hours)</th>
                    <th className={ALIGN_LEFT}>Rate ({currency})</th>
                    <th className={ALIGN_RIGHT}>Actions</th>
                  </tr>
                </THead>
                <TBody>
                  {hoursTiers.map((tier, index) => (
                    <tr key={index}>
                      <td>
                        <Input
                          type="number"
                          value={tier.maxHours}
                          onChange={(e) =>
                            updateTier(index, "maxHours", e.target.value)
                          }
                          disabled={noEditing}
                          style={{ width: 100 }}
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={tier.rate}
                          onChange={(e) =>
                            updateTier(index, "rate", e.target.value)
                          }
                          disabled={noEditing}
                          style={{ width: 120 }}
                        />
                      </td>
                      <td className={ALIGN_RIGHT}>
                        <Button
                          flavour="danger"
                          spaceBelow="none"
                          onClick={() => removeTier(index)}
                          disabled={noEditing}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </TBody>
              </Table>
              <Button
                spaceBelow="medium"
                onClick={addTier}
                disabled={noEditing}
                style={{ marginTop: 16 }}
              >
                Add Tier
              </Button>
              <HR spaceAbove="medium" spaceBelow="medium" />
              <H3>Overstay rate (per hour, beyond all tiers)</H3>
              <Triple
                currency={currency}
                serviceFeeRatio={serviceFeeRatio}
                error={errors.overstayRateAudPerHour}
                {...text({
                  name: "overstayRateAudPerHour",
                  validate: validateAUD,
                })}
                disabled={noEditing}
              />
            </>
          ) : (
            <>
              <H3>Overstay rate (per hour)</H3>
              <Triple
                currency={currency}
                serviceFeeRatio={serviceFeeRatio}
                error={errors.overstayRateAudPerHour}
                {...text({
                  name: "overstayRateAudPerHour",
                  validate: validateAUD,
                })}
                disabled={noEditing}
              />
            </>
          )}
        </>
      ) : isFixedSession ? (
        <>
          <H3>Active Schedule (flat-fee window)</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            On these days and hours, customers pay the flat session price below.
            Outside the active window, the card falls back to the hourly rate.
            Times are in the facility timezone ({installation.timeZoneName || "Australia/Sydney"}).
          </p>
          <div style={{ marginBottom: 24 }}>
            <Label>Active Days</Label>
            <div className={css({ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 })}>
              {enums.ISO_DAYS_OF_WEEK.map((d) => {
                const active = activeDaysOfWeek.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => !noEditing && toggleActiveDay(d.value)}
                    disabled={noEditing}
                    className={css({
                      padding: "8px 16px",
                      borderRadius: 6,
                      border: `2px solid ${active ? theme.blue : "#EAEDEF"}`,
                      backgroundColor: active ? theme.blue : "#fff",
                      color: active ? "#fff" : "#333",
                      fontWeight: active ? 600 : 400,
                      cursor: noEditing ? "not-allowed" : "pointer",
                      opacity: noEditing ? 0.5 : 1,
                      minWidth: 60,
                      transition: theme.transitionSnappy,
                    })}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
          <ColContainer columnTemplate={TPL_THIRDS}>
            <SelectInput
              label="Active from time"
              options={enums.ENTRY_EXIT_24HR_OPTIONS}
              error={errors.activeFromMinute}
              {...select("activeFromMinute")}
              disabled={noEditing}
            />
            <SelectInput
              label="Active to time"
              options={enums.ENTRY_EXIT_24HR_OPTIONS}
              error={errors.activeToMinute}
              {...select("activeToMinute")}
              disabled={noEditing}
            />
          </ColContainer>

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Fixed Session Pricing (active days)</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            Customer pays this flat session price for the fixed-fee duration
            below, within the active window.
          </p>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.fixedSessionPriceAud}
            {...text({
              name: "fixedSessionPriceAud",
              validate: validateAUD,
            })}
            disabled={noEditing}
          />

          <ColContainer columnTemplate={TPL_THIRDS}>
            <SelectInput
              label="Fixed-fee duration"
              error={errors.fixedSessionDurationMinutes}
              options={fixedSessionDurationOptions}
              placeholder={
                fixedSessionDurationOptions.length === 0
                  ? "Set active from/to time first"
                  : "Select duration"
              }
              {...select("fixedSessionDurationMinutes")}
              disabled={noEditing || fixedSessionDurationOptions.length === 0}
            />
          </ColContainer>
          <p style={{ marginTop: 4, marginBottom: 16, color: "#666", fontSize: 13 }}>
            How long the flat session fee covers (15-min steps, max = active-window
            length). Beyond this — or on early entry — the hourly overstay rate
            applies (or the full-day fee if no hourly rate is set).
          </p>

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay rate (per hour)</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            When set above 0, the customer is billed this rate for every hour
            past the fixed session, added on top of the session price. This takes
            precedence over the full-day fee below. Leave at 0 to charge the flat
            full-day fee instead.
          </p>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.overstayRateAudPerHour}
            {...text({
              name: "overstayRateAudPerHour",
              validate: validateAUD,
            })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay flagfall (flat fee, once per overstay)</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            Added once on top of the hourly overstay when the session is
            exceeded. Leave at 0 for no flagfall. Only applies when an hourly
            overstay rate is set above.
          </p>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.overstayFlagfallAud}
            {...text({ name: "overstayFlagfallAud", validate: validateAUD })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay maximum charge (per 24 hours)</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            Caps the hourly overstay so it never exceeds this amount per 24
            hours of overstay. Leave at 0 for no cap. Only applies when an hourly
            overstay rate is set above.
          </p>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.maximum24HourAud}
            {...text({ name: "maximum24HourAud", validate: validateAUD })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Full-day fee (used when no hourly overstay rate is set)</H3>
          <p style={{ marginBottom: 16, color: "#666" }}>
            Charged instead of the session price when the customer enters before
            the active window or stays past the fixed-fee duration (beyond the
            grace period). This replaces the session price — it is not added on
            top. Ignored when an hourly overstay rate is set above.
          </p>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.fixedSessionFullDayFeeAud}
            {...text({
              name: "fixedSessionFullDayFeeAud",
              validate: validateAUD,
            })}
            disabled={noEditing}
          />

          <HR spaceAbove="medium" spaceBelow="medium" />
          <ColContainer columnTemplate={TPL_THIRDS}>
            <SelectInput
              label="Grace period"
              options={enums.GRACE_PERIOD_OPTIONS}
              error={errors.overstayGraceMinutes}
              {...select("overstayGraceMinutes")}
              disabled={noEditing}
            />
          </ColContainer>
          <p style={{ marginTop: 4, marginBottom: 16, color: "#666", fontSize: 13 }}>
            Early entry or overstay within the grace period is forgiven — the
            customer still pays only the session price.
          </p>

          <HR spaceAbove="medium" spaceBelow="medium" />
          <Alert flavour="info">
            <strong>Preview:</strong>{" "}
            {activeDaysOfWeek.length > 0 ? (
              <>
                Flat {formatCurrency(Number(values.fixedSessionPriceAud) || 0, currency)} for the first{" "}
                {parseInt(values.fixedSessionDurationMinutes, 10)
                  ? formatDurationLabel(parseInt(values.fixedSessionDurationMinutes, 10))
                  : "?"}{" "}
                on{" "}
                {activeDaysOfWeek
                  .map((d) => enums.ISO_DAYS_OF_WEEK.find((x) => x.value === d)?.label)
                  .join(", ")}{" "}
                between{" "}
                {enums.ENTRY_EXIT_24HR_OPTIONS.find((o) => o.value === Number(values.activeFromMinute))?.label || "?"}
                {" – "}
                {enums.ENTRY_EXIT_24HR_OPTIONS.find((o) => o.value === Number(values.activeToMinute))?.label || "?"}
                . Early entry or going past that{" "}
                {Number(values.overstayRateAudPerHour) > 0 ? (
                  <>
                    is billed{" "}
                    {formatCurrency(Number(values.overstayRateAudPerHour) || 0, currency)}/hr
                    {" "}on top of the session price
                  </>
                ) : (
                  <>
                    incurs the full-day fee of{" "}
                    {formatCurrency(Number(values.fixedSessionFullDayFeeAud) || 0, currency)}{" "}
                    instead of the session price
                  </>
                )}
                . Inactive days are not bookable.
              </>
            ) : (
              <>Pick at least one active day to see the preview.</>
            )}
          </Alert>
        </>
      ) : (
        <>
          <H3>Hourly rate (per hour)</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.bookingRateAudPerDay}
            {...text({
              name: "bookingRateAudPerHour",
              validate: validateAUD,
            })}
            disabled={noEditing}
          />

          {values.type === "HOURLY" && (
            <>
              <HR spaceAbove="medium" spaceBelow="medium" />
              <H3>Custom Hourly Rate Tiers</H3>
              <p style={{ marginBottom: 16, color: "#666" }}>
                Optionally configure per-range hourly rates. Tiers can be set
                on 30-minute boundaries. Hours beyond all tiers use the
                standard hourly rate above.
              </p>
              <label
                className={css({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: noEditing ? "not-allowed" : "pointer",
                  opacity: noEditing ? 0.5 : 1,
                  marginBottom: 16,
                  userSelect: "none",
                })}
              >
                <span
                  role="switch"
                  aria-checked={showCustomHourlyRates}
                  tabIndex={0}
                  onClick={() => {
                    if (noEditing) return;
                    if (showCustomHourlyRates) {
                      setCustomHourlyRates([]);
                    }
                    setShowCustomHourlyRates(!showCustomHourlyRates);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      if (noEditing) return;
                      if (showCustomHourlyRates) {
                        setCustomHourlyRates([]);
                      }
                      setShowCustomHourlyRates(!showCustomHourlyRates);
                    }
                  }}
                  className={css({
                    position: "relative",
                    display: "inline-block",
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor: showCustomHourlyRates
                      ? theme.blue
                      : "#ccc",
                    transition: "background-color 0.2s",
                    flexShrink: 0,
                    "::after": {
                      content: '""',
                      position: "absolute",
                      top: 2,
                      left: showCustomHourlyRates ? 22 : 2,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    },
                  })}
                />
                <span style={{ fontSize: 14 }}>
                  {showCustomHourlyRates
                    ? "Custom tiers enabled"
                    : "Custom tiers disabled"}
                </span>
              </label>
              {showCustomHourlyRates && (
                <>
                  <Table>
                    <THead>
                      <tr>
                        <th className={ALIGN_LEFT}>Range</th>
                        <th className={ALIGN_LEFT}>Up to hour</th>
                        <th className={ALIGN_LEFT}>
                          Rate ({currency})
                        </th>
                        <th className={ALIGN_RIGHT}>Actions</th>
                      </tr>
                    </THead>
                    <TBody>
                      {customHourlyRates.map((tier, index) => {
                        const from = index === 0 ? 0 : Number(customHourlyRates[index - 1].upToHour);
                        return (
                          <tr key={index}>
                            <td style={{ color: "#888", whiteSpace: "nowrap" }}>
                              {formatHourLabel(from)}–{formatHourLabel(Number(tier.upToHour))}
                            </td>
                            <td>
                              <Input
                                type="number"
                                min={0.5}
                                step={0.5}
                                value={tier.upToHour}
                                onChange={(e) => {
                                  const raw = parseFloat(e.target.value);
                                  const val = Number.isFinite(raw)
                                    ? Math.max(0.5, raw)
                                    : 0.5;
                                  const snapped = Math.round(val * 2) / 2;
                                  updateCustomHourlyRate(
                                    index,
                                    "upToHour",
                                    snapped,
                                  );
                                }}
                                disabled={noEditing}
                                style={{ width: 100 }}
                              />
                            </td>
                            <td>
                              <Input
                                type="text"
                                value={tier.rate}
                                onChange={(e) =>
                                  updateCustomHourlyRate(
                                    index,
                                    "rate",
                                    e.target.value,
                                  )
                                }
                                disabled={noEditing}
                                style={{ width: 120 }}
                              />
                            </td>
                            <td className={ALIGN_RIGHT}>
                              <Button
                                flavour="danger"
                                spaceBelow="none"
                                onClick={() => removeCustomHourlyRate(index)}
                                disabled={noEditing}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </TBody>
                  </Table>
                  {duplicateUpToHour !== null && (
                    <Alert flavour="danger" spaceAbove="xsmall" spaceBelow="none">
                      Duplicate "Up to hour" value: {duplicateUpToHour}. Each tier must have a unique "Up to hour".
                    </Alert>
                  )}
                  {misalignedUpToHour !== null && (
                    <Alert flavour="danger" spaceAbove="xsmall" spaceBelow="none">
                      "Up to hour" value {misalignedUpToHour} must be a positive multiple of 0.5.
                    </Alert>
                  )}
                  <Button
                    spaceBelow="medium"
                    onClick={addCustomHourlyRate}
                    disabled={noEditing}
                    style={{ marginTop: 16 }}
                  >
                    Add Tier
                  </Button>
                </>
              )}
            </>
          )}
        </>
      )}

      {isFixedSession ? null : (
        <>
          <HR spaceAbove="medium" spaceBelow="medium" />
          {isDaily || isMonthly ? (
            <H3>Overstay maximum charge (per day of overstay)</H3>
          ) : isTieredHours ? (
            <H3>Overstay maximum charge (per 24 hours beyond tiers)</H3>
          ) : (
            <H3>Maximum charge (per 24 hours)</H3>
          )}
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.maximum24HourAud}
            {...text({ name: "maximum24HourAud", validate: validateAUD })}
            disabled={noEditing}
          />
        </>
      )}

      {isTieredHours && (
        <>
          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>Overstay grace period</H3>
          <ColContainer columnTemplate={TPL_THIRDS}>
            <SelectInput
              label="Overstay grace period"
              options={enums.GRACE_PERIOD_OPTIONS}
              error={errors.overstayGraceMinutes}
              {...select("overstayGraceMinutes")}
              disabled={noEditing}
            />
          </ColContainer>
        </>
      )}

      <HR spaceAbove="medium" spaceBelow="medium" />
      <H3>Schedules</H3>
      {isDaily ? (
        <>
          <ColContainer columnTemplate={TPL_THIRDS}>
            <SelectInput
              label="Entry after time"
              options={enums.ENTRY_EXIT_24HR_OPTIONS}
              error={errors.entryAfterTime}
              {...select("entryAfterTime")}
              disabled={noEditing}
            />
            <SelectInput
              label="Exit before time"
              error={errors.exitBeforeTime}
              options={enums.ENTRY_EXIT_24HR_OPTIONS}
              {...select("exitBeforeTime")}
              disabled={noEditing}
            />
            <SelectInput
              label="Grace period after booking"
              options={enums.GRACE_PERIOD_OPTIONS}
              error={errors.overstayGraceMinutes}
              {...select("overstayGraceMinutes")}
              disabled={noEditing}
            />
          </ColContainer>
        </>
      ) : null}
      <ColContainer columnTemplate={TPL_THIRDS}>
        <SelectInput
          label={
            isDaily || isMonthly ? "Maximum overstay period" : "Expires after"
          }
          options={enums.EXPIRY_HOURS_OPTIONS}
          error={errors.hoursUntilExpiry}
          {...select("hoursUntilExpiry")}
          disabled={noEditing}
        />
      </ColContainer>

      {isDaily || isMonthly || isTieredHours || isFixedSession ? null : (
        <>
          <HR spaceAbove="medium" spaceBelow="medium" />
          <H3>
            Charge after {formatHours(values.hoursUntilExpiry)} with no exit
            (expiry)
          </H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.chargeOnExpiryAud}
            {...text({ name: "chargeOnExpiryAud", validate: validateAUD })}
            disabled={noEditing}
          />
        </>
      )}

      <HR spaceAbove="medium" spaceBelow="medium" />

      <H3>Valet</H3>
      <Table>
        <TBody>
          <FieldRow
            key={"isValet"}
            {...isValetCheckboxProps}
            onChange={onIsValetChange}
            name={"Is Valet"}
            disabled={false}
          />
          {isCardItselfValet ? (
            <FieldRow
              key={"valetPayLater"}
              {...checkbox("valetPayLater")}
              name={"Allow Pay Later"}
              disabled={noEditing || !canPayLater}
            />
          ) : null}
        </TBody>
      </Table>

      {isCardItselfValet ? (
        <>
          <H3>Valet request fee</H3>
          <Triple
            currency={currency}
            serviceFeeRatio={serviceFeeRatio}
            error={errors.valetRequestFeeAud}
            {...text({ name: "valetRequestFeeAud", validate: validateAUD })}
            disabled={noEditing}
          />

          <ColContainer columnTemplate={TPL_THIRDS}>
            <Input
              label="Free requests per day"
              disabled={noEditing}
              {...number({ name: "valetFreeRequestsPerDay" })}
            />
          </ColContainer>
        </>
      ) : null}


      <HR spaceAbove="medium" spaceBelow="medium" />

      <H3>Rate Card Limits</H3>

      <Table>
        <TBody>
          {" "}
          {isFixedPackage ? (
            <p style={{ marginBottom: 16, color: "#666" }}>
              Starts At / Expires At are taken from the Package Dates above for
              this calculation mode.
            </p>
          ) : (
            <>
              <DateInput
                floatPicker
                label="Starts At"
                placeholder="Enter start date"
                autoComplete="off"
                displayFormat="ddd, D MMM YYYY"
                isOutsideRange={(x) => false}
                type="text"
                {...raw({
                  name: "startsAt",
                })}
              />
              <DateInput
                floatPicker
                label="Expires At"
                placeholder="Enter expiry date"
                autoComplete="off"
                displayFormat="ddd, D MMM YYYY"
                isOutsideRange={(x) => false}
                type="text"
                {...raw({
                  name: "expiresAt",
                })}
              />
            </>
          )}
          <Input
            label="Bookings Limit"
            disabled={false}
            {...number({ name: "bookingsLimit" })}
          />
          <FieldRow
            key={"ignoreGlobalCapacity"}
            {...checkbox("ignoreGlobalCapacity")}
            name={"Ignore Global Capacity"}
            disabled={false}
          />
          <ButtonsRow
            key={"resetLimit"}
            name={"resetLimit"}
            options={['OFF', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']}
            register={radio}
            title={"Reset Limit"}
          />
          {rateCardStatus != null && rateCardStatus.bookings != null && (
            <div className={css({
              display: "flex",
              gap: 8,
              flexDirection: "column",
              marginTop: 16
            })}>
              {rateCard.resetLimit !== 'DAILY' && <p
                style={{
                  fontSize: 12,
                  color: "grey",
                  marginTop: 0,
                  lineHeight: 0,
                }}
              >
                Daily Bookings: {rateCardStatus.todayBookings}
              </p>}
              {<p
                style={{
                  fontSize: 12,
                  color: "grey",
                  marginTop: 0,
                  lineHeight: 0,
                }}
              >
                <span className={css({ textTransform: 'capitalize' })}>{rateCard.resetLimit === 'OFF' ? 'Total' : rateCard.resetLimit.toLowerCase()}</span> Bookings: {rateCardStatus.bookings}
              </p>}
            </div>
          )}
        </TBody>
      </Table>

      <H3>Public Rate Card Info</H3>
      <Table>
        <TBody>
          <FieldRow
            key={"isPublic"}
            {...checkbox("isPublic")}
            name={"Is Public"}
            disabled={false}
          />

          <FieldRow
            key={"parklioBayAssignment"}
            {...checkbox("parklioBayAssignment")}
            name={"Parklio bay assignment"}
            disabled={false}
          />

          {!isMonthly && (
            <FieldRow
              key={"multiDates"}
              {...checkbox("multiDates")}
              name={"Multi-Dates (multi-day)"}
              disabled={false}
            />
          )}

          <FieldRow
            key={"allowFutureDate"}
            {...checkbox("allowFutureDate")}
            name={"Allow Future Bookings"}
            disabled={false}
          />

          <FieldRow
            key={"selfPark"}
            {...checkbox("selfPark")}
            name={"Include Self Park flow"}
            disabled={false}
          />

          <FieldRow
            key={"includeCarType"}
            {...checkbox("includeCarType")}
            name={"Include Car Type"}
            disabled={false}
          />

          <FieldRow
            key={"includeCarNotes"}
            {...checkbox("includeCarNotes")}
            name={"Include Car Notes"}
            disabled={false}
          />

          {values.type === "DAILY" && (
            <tr>
              <td>Calculation Mode</td>
              <td>
                <SelectInput
                  options={enums.getRateCardCalculationModes(
                    isDaily,
                    values.multiDates,
                    rateCard?.calculationMode,
                  )}
                  error={errors.calculationMode}
                  {...select("calculationMode")}
                />
              </td>
            </tr>
          )}
          <TextInputRow
            key={"publicUrl"}
            {...text("publicUrl")}
            rows={2}
            name={"Public Url"}
          />
          {bookingUiV2Enabled && (
            <TextInputRow
              key={"homeTitle"}
              {...text("homeTitle")}
              rows={2}
              name={"Home page title"}
            />
          )}
          <TextAreaRow
            {...text("homeText")}
            rows="3"
            cols="30"
            key={"homeText"}
            name={"Home page text (multi line)"}
          />

          <TextAreaRow
            {...text("lotFullText")}
            rows="3"
            cols="30"
            key={"lotFullText"}
            name={"Message for when lot is full (multi line)"}
          />

          <FieldRow
            key={"stopPublicBookingWhenLotFull"}
            {...checkbox("stopPublicBookingWhenLotFull")}
            name={"Stop public bookings when lot full"}
            disabled={false}
          />
        </TBody>
      </Table>

      {values.isPublic && (
        <Container spaceAbove="medium">
          <H3>Dynamic Public URL</H3>
          <Table>
            <TBody>
              <FieldRow
                key={"isDynamicPublicUrl"}
                {...checkbox("isDynamicPublicUrl")}
                name={"Enable Dynamic Public URL"}
                disabled={noEditing}
              />
              {values.isDynamicPublicUrl && (
                <>
                  <tr>
                    <td>
                      <span className={ALIGN_LEFT}>Update Hour (0-23)</span>
                    </td>
                    <td className={ALIGN_RIGHT}>
                      <SelectInput
                        options={Array.from({ length: 24 }, (_, i) => ({
                          label: `${i}:00`,
                          value: i,
                        }))}
                        {...select("dynamicPublicUrlUpdateHour")}
                        disabled={noEditing}
                      />
                    </td>
                  </tr>
                  <TextAreaRow
                    {...text({
                      name: "dynamicPublicUrlEmails",
                      validate: validateDynamicPublicUrlEmails,
                    })}
                    rows="3"
                    cols="30"
                    key={"dynamicPublicUrlEmails"}
                    name={"Notification Emails (comma-separated)"}
                    disabled={noEditing}
                    placeholder="email1@example.com, email2@example.com"
                  />
                  {rateCard?.dynamicPublicUrlUpdatedAt && (
                    <tr>
                      <td>
                        <span className={ALIGN_LEFT}>Last URL Update</span>
                      </td>
                      <td className={ALIGN_RIGHT}>
                        {new Date(rateCard.dynamicPublicUrlUpdatedAt).toLocaleString()}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </TBody>
          </Table>
        </Container>
      )}

      {rateCard ? (
        <ReactToPrint
          content={() => componentRef.current}
          onAfterPrint={() => {
            setQrcode(false);
            setIsHotel57(false);
          }}
        >
          <PrintContextConsumer>
            {({ handlePrint }) => (
              <>
                <Button onClick={() => handlePrintFn(handlePrint)}>
                  Print QR code
                </Button>
                {installation?.brandingKey === "rydges-sydney-central" && (
                  <Button
                    onClick={() => handlePrintFn(handlePrint, true)}
                    style={{ marginLeft: "1rem" }}
                  >
                    Print 57Hotel QR code
                  </Button>
                )}
              </>
            )}
          </PrintContextConsumer>
        </ReactToPrint>
      ) : null}

      <RateCardExtraContainer
        rateCardExtras={rateCardExtras}
        setRateCardExtras={setRateCardExtras}
        brandingKey={installation?.brandingKey}
      />

      {qrcode ? (
        <div ref={componentRef}>
          <Table>
            <THead>
              <tr>
                <h2
                  className={css({
                    textAlign: "center",
                    paddingTop: "60px",
                    fontFamily: "monospace",
                    marginBottom: "60px",
                  })}
                >
                  {rateCard.name}
                </h2>
              </tr>
            </THead>
            <TBody
              className={css({
                textAlign: "center",
                fontFamily: "monospace",
              })}
            >
              <QRCode
                value={`https://public.hotelparking.com.au/${installation.publicConsoleSlug}/${rateCard.publicUrl}${isHotel57 ? "?h57=t" : ""}`}
              />
              <br />
              <h3>book parking on this link: </h3>
              <h3>
                https://public.hotelparking.com.au/
                {installation.publicConsoleSlug}/{rateCard.publicUrl}
                {isHotel57 ? "?h57=t" : ""}
              </h3>
            </TBody>
          </Table>
        </div>
      ) : null}

      <HR spaceAbove="medium" spaceBelow="medium" />
      <H3>Required Fields</H3>
      <Container>
        <Table>
          <THead>
            <tr>
              <th className={ALIGN_LEFT}>Field</th>
              <th className={ALIGN_RIGHT}>Required</th>
            </tr>
          </THead>
          <colgroup>
            <col className={css({ width: "80%" })} />
            <col className={css({ width: "20%" })} />
          </colgroup>
          <TBody>{dataRows}</TBody>
        </Table>
      </Container>

      <Container spaceAbove="medium" spaceBelow="medium">
        <H3>VIP Guest Card</H3>
        <Table>
          <TBody>
            <FieldRow
              key={"isVip"}
              {...checkbox("isVip")}
              name={"Is VIP"}
              disabled={false}
            />

            <TextInputRow
              key={"vipEmailRecipient"}
              {...email("vipEmailRecipient")}
              rows={2}
              name={"Email Recipient"}
              placeholder="name@email.com"
              error={errors.vipEmailRecipient}
            />

            {vipRoutingEnabled ? (
              <tr key={"defaultVipContactId"}>
                <td>
                  <span className={ALIGN_LEFT}>Default Contact (Valet)</span>
                </td>
                <td className={ALIGN_RIGHT}>
                  <SelectInput
                    options={[
                      { label: "No default booking contact selected", value: "" },
                      ...vipContacts.map((c) => ({
                        value: c.id,
                        label: c.name,
                      })),
                    ]}
                    {...select("defaultVipContactId")}
                  />
                </td>
              </tr>
            ) : null}

            {isCardItselfValet ? null : (
              <>
                <FieldRow
                  key={"hasValetOption"}
                  {...checkbox("hasValetOption")}
                  name={"Has Valet Option"}
                  disabled={false}
                />

                {values.hasValetOption ? (
                  <tr>
                    <td>
                      <span className={ALIGN_LEFT}>Valet Guest Card</span>
                    </td>
                    <td className={ALIGN_RIGHT}>
                      <SelectInput
                        placeholder="Select a guest card"
                        options={valetTargetOptions}
                        error={valetGuestCardMissing}
                        {...select("valetGuestCardLineageId")}
                      />
                      {valetGuestCardMissing ? (
                        <div
                          className={css({
                            color: theme.flavours.danger.highlightColor,
                            fontSize: 14,
                            marginTop: -8,
                            marginBottom: 8,
                            textAlign: "right",
                          })}
                        >
                          Select a "Valet Guest Card" when "Has Valet Option" is enabled.
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </>
            )}
          </TBody>
        </Table>
      </Container>

      <Container spaceAbove="medium">
        <H3 id="public-ratecard">Public Guest Card</H3>
        <DefaultPublicPrompt
          installation={installation}
          rateCard={rateCard}
          {...raw("defaultPublic")}
          disabled={noEditing || !optionals.defaultPublic}
        />
      </Container>

      <Container spaceAbove="medium">
        <H3 id="public-ratecard">Exiting Guest Card</H3>
        <DefaultExitPrompt
          {...raw("defaultOnExit")}
          disabled={noEditing || !optionals.defaultOnExit}
        />
      </Container>

      <HR spaceAbove="medium" spaceBelow="medium" />
      <PermissionOnly permissions={PERMISSIONS.GUEST_CARDS_MANAGE}>
        <Container>
          <Button block type="submit" disabled={!canSubmit} onClick={_onSubmit}>
            {isCreating ? "Create" : "Update card"}
          </Button>{" "}
          <NakedButton block flavour="gray" onClick={onCancel}>
            Cancel
          </NakedButton>
        </Container>
      </PermissionOnly>
    </div>
  );
}
